import {
  AccountBalanceChangedExceptionPushEvent,
  AccountBalanceChangedPushEvent,
} from '@rwwa/tabtouch-push-contract'
import { toastIds } from '@mobi/account/Utils/constants'
import { isSelfServePaymentMethod } from '@mobi/component-library/Areas/Deposit/helpers'
import { addToast, removeToast } from '@mobi/component-library/Feedback/Toast'

const FeedbackBalanceChangeSuccess = () =>
  addToast({
    id: toastIds.depositSuccess,
    isDismissable: true,
    title: 'Your balance has been updated.',
    icon: 'success',
  })

const FeedbackWithdrawal = (
  transactionType: AccountBalanceChangedExceptionPushEvent['transactionType']
) => {
  removeToast({ id: toastIds.pendingWithdrawal })

  switch (transactionType) {
    case 'Withdrawal Cancel':
      addToast({
        theme: 'dark',
        id: toastIds.withdrawalError,
        isDismissable: true,
        title: 'Withdrawal cancelled.',
        subtitle: 'Your balance has been restored.',
        icon: 'info',
        timeoutMs: 6_000, // 6s
      })
      break
  }
}

const FeedbackWithdrawalError = () => {
  removeToast({ id: toastIds.pendingWithdrawal })
  addToast({
    id: toastIds.withdrawalError,
    isDismissable: true,
    title:
      'We were unable to process your withdraw request. You have insufficient funds to withdraw.',
    icon: 'error',
    timeoutMs: 6_000, // 6s
  })
}

const FeedbackDepositError = (exception: AccountBalanceChangedExceptionPushEvent['exception']) => {
  let message = ''
  let title = ''

  switch (exception) {
    case 'DepositPrecommLimit':
      title = 'Daily deposit limit exceeded'
      message =
        'Your deposit exceeded your daily limit. We have refunded the amount. Please try again with a value equal to or lower than your daily limit.'
      break
    case 'DepositMinNotMet':
      title = 'Minimum value not reached'
      message =
        'Your deposit was lower than the minimum deposit value. We have refunded the amount. Please try again.'
      break
    case 'DepositAmtLimit':
      title = 'Daily deposit limit exceeded'
      message =
        'Your deposit exceeds your daily deposit limit. Please try again with a lower amount.'
      break
    case 'DepositVolLimit':
      title = 'Daily deposit transaction limit reached'
      message =
        'You have reached your maximum number of deposits for today. Please try again tomorrow.'
      break
    case 'DepositBpayBlocked':
      title = 'BPay deposit unavailable'
      message =
        'BPay deposits are currently unavailable. Please use a different payment method or call us on 1300 36 36 88.'
      break
    case 'DepositNameMatching':
      title = 'Name does not match'
      message =
        "Sorry, the name on your bank account doesn't match the name on your TABtouch account. Please try a different bank account for your PayID deposit."
      break
    case 'DepositBankAcctLimit':
      title = 'Bank account limit reached'
      message =
        'You have reached the maximum number of bank accounts. Please call us on 1300 36 36 88 for assistance'
      break
    case 'DepositBankAcctOther':
    case 'DepositExceptionOthr':
    default:
      title = 'Your deposit could not be completed'
      message =
        'Please try again. If the issue persists, please contact your bank to check your account details or call us on 1300 36 36 88.'
      break
  }

  addToast({
    id: toastIds.depositError,
    isDismissable: true,
    title: title,
    subtitle: message,
    icon: 'error',
    timeoutMs: 6_000, // 6s
  })
}

export const FeedbackBalanceChange = (
  balanceChangeEvent: AccountBalanceChangedPushEvent | AccountBalanceChangedExceptionPushEvent
) => {
  /*
   * We only want to show feedbacks for self serve deposits.
   * The feedback for internal deposits are already handled.
   */
  if (
    balanceChangeEvent.paymentMethod &&
    !isSelfServePaymentMethod(balanceChangeEvent.paymentMethod)
  )
    return

  if ('exception' in balanceChangeEvent) {
    const depositExceptionCodes = [
      'DepositMinNotMet',
      'DepositPrecommLimit',
      'DepositExceptionOthr',
      'DepositBpayBlocked',
      'DepositAmtLimit',
      'DepositVolLimit',
      'DepositNameMatching',
      'DepositBankAcctLimit',
      'DepositBankAcctOther',
    ]
    if (depositExceptionCodes.includes(balanceChangeEvent.exception)) {
      FeedbackDepositError(balanceChangeEvent.exception)
    }

    if (balanceChangeEvent.exception === 'WithdrawInsuffFunds') {
      FeedbackWithdrawalError()
    }
    return
  }

  if (balanceChangeEvent.transactionType?.includes('Withdrawal')) {
    // Delay the feedback to allow the user to see the pending withdrawal toast
    setTimeout(() => FeedbackWithdrawal(balanceChangeEvent.transactionType), 2000)

    return
  }

  FeedbackBalanceChangeSuccess()
}
